import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LearningSessionsApi, MemriseApiError } from 'src/api';
import { LearningSessionsDomainResponse } from 'src/api/learningSessions';
import {
  ApiSessionSourceType,
  LearnableWithProgress,
  LearningSessionError,
  SessionSourceType,
  TestSettings,
  WebSessionType,
} from 'src/api/learningSessions/types';
import { advanceToNextTrackWordlist } from 'src/apps/VocabTracks/advanceToNextTrackWordlist';
import { useEligibility } from 'src/hooks/useEligibility';
import { useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { SetErrorAction } from './learningSessionReducer';
import { makeLearnablesWithProgress } from './utils';
import { CODE_TO_MESSAGE, LEARN_WORDLISTS_DOESNT_LOAD, msgs } from './errors';

interface SessionLearnablesWithProgress {
  learnablesWithProgress: LearnableWithProgress[];
  learnablesWithProgressLookup: { [k: string]: LearnableWithProgress };
  levelName: string | null;
  levelId: number | null;
  sessionSourceName: string | null;
  sessionSourceId?: number;
  sessionTemplateId?: number;
  sessionSourceType?: SessionSourceType;
  parentSourceId?: number;
  languagePairId: number | null;
  loading: boolean;
  numItemsDueForReview: number | null;
  testSettings: TestSettings | null;
}

type useSessionLearnablesWithProgressTypes = {
  sessionType: WebSessionType;
  sessionSourceType: ApiSessionSourceType;
  sessionSourceId: number;
  learningSessionDispatch: (action: SetErrorAction) => void;
  sessionId: string;
};

// This eventually wants to be useSessionCards
// For now, we only show one learnable card per learnable
// so we can return learnablesWithProgress without an
// issue
export function useSessionLearnablesWithProgress({
  sessionType,
  sessionSourceType,
  sessionSourceId,
  learningSessionDispatch,
  sessionId,
}: useSessionLearnablesWithProgressTypes): SessionLearnablesWithProgress {
  const [learnablesWithProgress, setLearnablesWithProgress] = useState<LearnableWithProgress[]>([]);
  const [error, setError] = useState<LearningSessionError | null>(null);
  const isCreatingSession = useRef(false);
  const [loading, setLoading] = useState(true);
  const [testSettings, setTestSettings] = useState<TestSettings | null>(null);
  const [sessionSourceInfo, setSessionSourceInfo] = useState<
    LearningSessionsDomainResponse['sessionSourceInfo'] | null
  >(null);
  const { experienceType } = useExperienceContext();
  const { loading: isUserLoading } = useUserContext();
  const queryClient = useQueryClient();
  const languagePairId = getLanguagePairId();
  const { isEligibleForTracksAndThemes, isLoading: isEligibilityLoading } =
    useEligibility(languagePairId);
  const hasAdvancedWordlist = useRef(false);
  const [finishedWordlistCode, setFinishedWordlistCode] = useState<string | null>(null);

  const createSession = useCallback(async () => {
    const {
      learnables: learnablesArray,
      progress,
      sessionSourceInfo,
      settings,
    } = await LearningSessionsApi.startLearningSession(sessionType, {
      sessionSourceType: sessionSourceType,
      sessionSourceId: sessionSourceId,
    });
    setSessionSourceInfo(sessionSourceInfo);
    setTestSettings(settings);

    if (learnablesArray.length === 0) {
      // Should now be handled on backend,
      // Please remove if unseen (June 2021)
      setError({
        header: msgs.whoops,
        description: msgs.notEnoughItemsToReview,
      });
    } else {
      const learnablesWithProgress = makeLearnablesWithProgress(learnablesArray, progress);

      setLearnablesWithProgress(learnablesWithProgress);
      setLoading(false);
    }
  }, [sessionSourceId, sessionSourceType, sessionType]);

  const setErrorForCode = useCallback((code?: string) => {
    if (code && CODE_TO_MESSAGE[code]) {
      setError(CODE_TO_MESSAGE[code]);
    } else {
      setError(CODE_TO_MESSAGE.DEFAULT as LearningSessionError);
    }
  }, []);

  useEffect(() => {
    /* istanbul ignore next: should not happen outside the local development in React strict mode */
    if (!experienceType || !languagePairId || isCreatingSession.current) return;

    void (async function () {
      isCreatingSession.current = true;

      try {
        await createSession();
      } catch (error) {
        const { code } = error as MemriseApiError;

        if (code === LEARN_WORDLISTS_DOESNT_LOAD) {
          // Whether this learner follows a vocab track depends on the user fetch,
          // which can still be in flight, so hand the decision to the effect below.
          setFinishedWordlistCode(code);
        } else {
          setErrorForCode(code);
        }
      } finally {
        isCreatingSession.current = false;
      }
    })();
  }, [createSession, experienceType, languagePairId, setErrorForCode]);

  useEffect(() => {
    if (
      !finishedWordlistCode ||
      isUserLoading ||
      isEligibilityLoading ||
      hasAdvancedWordlist.current
    )
      return;

    hasAdvancedWordlist.current = true;

    if (!isEligibleForTracksAndThemes || !languagePairId) {
      setErrorForCode(finishedWordlistCode);
      return;
    }

    // A vocab-track learner reaching a finished list should carry on with the track
    // rather than hit a dead end, so advance once and retry the session before
    // falling back to the error card and its list-picker link.
    void (async function () {
      try {
        if ((await advanceToNextTrackWordlist(languagePairId, queryClient)) === 'advanced') {
          await createSession();
        } else {
          setErrorForCode(finishedWordlistCode);
        }
      } catch {
        setErrorForCode(finishedWordlistCode);
      }
    })();
  }, [
    createSession,
    finishedWordlistCode,
    isEligibilityLoading,
    isEligibleForTracksAndThemes,
    isUserLoading,
    languagePairId,
    queryClient,
    setErrorForCode,
  ]);

  useEffect(() => {
    if (error) {
      learningSessionDispatch({ type: 'SET_ERROR', error });
    }
  }, [
    error,
    learningSessionDispatch,
    sessionId,
    sessionType,
    sessionSourceId,
    sessionSourceInfo?.levelId,
    sessionSourceType,
  ]);

  const learnablesWithProgressLookup = useMemo(
    () =>
      learnablesWithProgress.reduce(
        (accumulator, lwp) => ({
          ...accumulator,
          [lwp.learnable.id]: lwp,
        }),
        {},
      ),
    [learnablesWithProgress],
  );

  return {
    learnablesWithProgress,
    learnablesWithProgressLookup,
    loading,
    testSettings,
    levelName: sessionSourceInfo?.levelName ?? null,
    levelId: sessionSourceInfo?.levelId ?? null,
    sessionSourceName: sessionSourceInfo?.name ?? null,
    sessionSourceId: sessionSourceInfo?.sourceId,
    sessionTemplateId: sessionSourceInfo?.templateId,
    sessionSourceType: sessionSourceInfo?.sourceType,
    parentSourceId: sessionSourceInfo?.parentSourceId,
    languagePairId: sessionSourceInfo?.languagePairId ?? null,
    numItemsDueForReview: sessionSourceInfo?.numDueForReview ?? null,
  };
}
